import { IMenuItem } from '../../Common';
import cc from 'classcat';
import { useEffect, useRef, useState } from 'react';
import { NewCheckbox } from '../NewCheckbox';

interface SearchableDropdownMultiProps {
    label?: string;
    id?: string;
    menuItems: IMenuItem[];
    selectedValues: string[];
    className?: string;
    placeholder?: string;
    isError?: string | boolean;
    isRequired?: boolean;
    refInputElement?: (inputElement: HTMLInputElement) => void;
    onChange: (selectedValues: string[]) => void;
    disabled?: boolean;
    hasError?: boolean;
}

export const SearchableDropdownMulti = ({
    label,
    id,
    menuItems,
    selectedValues,
    className,
    placeholder,
    isRequired,
    onChange,
    refInputElement,
    disabled,
    hasError = false,
}: SearchableDropdownMultiProps) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [filteredItems, setFilteredItems] = useState(menuItems);
    const [searchInput, setSearchInput] = useState('');
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        setFilteredItems(menuItems);
    }, [menuItems]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenMenu(false);
                validateDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedValues, isRequired]);

    const handleCheckboxChange = (value: string, checked: boolean) => {
        if (checked) {
            onChange([...selectedValues, value]);
        } else {
            onChange(selectedValues.filter(v => v !== value));
        }
    };

    const validateDropdown = () => {
        const hasError = isRequired && selectedValues.length === 0;
        if (hasError) setError(hasError);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
        if (!openMenu) setOpenMenu(true);
        const val = e.target.value.toLocaleLowerCase();
        if (val === '') setFilteredItems(menuItems);
        else setFilteredItems(menuItems.filter(it => it.label.toLocaleLowerCase().includes(val)));
    };

    const getDisplayText = () => {
        if (selectedValues.length === 0) return '';

        const selectedItems = selectedValues
            .map(value => menuItems.find(m => m.value === value))
            .filter(item => item !== undefined)
            .map(item => item!.label);

        if (selectedItems.length <= 3) {
            return selectedItems.join(', ');
        }

        return selectedItems.slice(0, 3).join(', ') + '...';
    };

    useEffect(() => {
        if (isRequired) {
            validateDropdown();
        } else {
            setError(false);
        }
    }, [selectedValues, isRequired]);

    return (
        <div ref={dropdownRef} className={cc(['dropdown', className, { 'dropdown--opened': openMenu }])}>
            {label && (
                <label htmlFor={id} className="dropdown__label paragraph-4">
                    {label}
                    {isRequired ? <span className="input-style_is_req">*</span> : ''}
                </label>
            )}
            <button
                className={cc(['dropdown__select dropdown__select--search', { 'dropdown__select--error': hasError || error }])}
                onClick={() => setOpenMenu(!openMenu)}
                type="button"
                disabled={disabled}
            >
                <div className={cc(['dropdown__selected-item', openMenu && filteredItems.length > 0 && 'dropdown__selected-item--active'])}>
                    <input
                        className="dropdown__search paragraph-4"
                        value={openMenu ? searchInput : getDisplayText()}
                        onChange={onInputChange}
                        onFocus={() => {
                            setOpenMenu(true);
                            setSearchInput('');
                        }}
                        placeholder={placeholder}
                        ref={refInputElement}
                        disabled={disabled}
                    />
                    {openMenu && filteredItems.length > 0 ? (
                        <img src="/svg/arrow-up.svg" alt="Arrow Down" />
                    ) : (
                        <img src="/svg/arrow-down.svg" alt="Arrow Up" />
                    )}
                </div>
            </button>
            {openMenu && filteredItems.length > 0 && (
                <div className="dropdown__menu dropdown__menu--multi">
                    {filteredItems.map(option => (
                        <div
                            key={option.value}
                            className="dropdown__menu--item dropdown__menu--item-checkbox"
                            onClick={e => {
                                e.stopPropagation();
                                handleCheckboxChange(option.value.toString(), !selectedValues.includes(option.value.toString()));
                            }}
                        >
                            <NewCheckbox
                                id={`${id}-${option.value}`}
                                label={option.label}
                                checked={selectedValues.includes(option.value.toString())}
                                handleChange={e => {
                                    e.stopPropagation();
                                    handleCheckboxChange(option.value.toString(), e.target.checked);
                                }}
                                className="dropdown__menu--checkbox"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
